import { getPostApiCertificationResetMutationOptions } from '@smartsash/api-client';
import { useEntityMutation } from '@smartsash/server-ops';
import { Icon, openModal } from '@smartsash/ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface ResetCertificationSubject {
  userId: string;
  institutionId: string;
  /** Named in the confirmation title so an administrator can see whose record they are revoking. */
  name: string;
  /** Already translated: the two entry points describe different scopes, on purpose. */
  description: string;
}

/**
 * One certification scope. **`null` is not "everything"** — it names the institution-wide
 * programme and nothing else. `CertificationAdminService.ResetAsync` filters records
 * `c.LabId == null` for it, so laboratory-scoped certifications survive a null reset.
 *
 * Pass the row's own laboratory where the row knows one; pass `null` only where the caller
 * genuinely means the institution-wide certification, and say so in `description`.
 */
interface ScopedReset {
  labId: string | null;
  allScopes?: never;
}

/**
 * Every certification the person holds — each laboratory's and the institution-wide one (HS-1491).
 * For a caller with no scope of its own, such as the users list. A separate branch rather than a
 * flag beside `labId`, because the API rejects a request carrying both with `400`.
 */
interface AllScopesReset {
  allScopes: true;
  labId?: never;
}

export type ResetCertificationTarget = ResetCertificationSubject & (ScopedReset | AllScopesReset);

/**
 * The confirm-and-revoke flow behind every "Reset certification" entry.
 *
 * One flow, two entry points — the dashboard's certification panel and the users list — which is
 * the same shape as `useRegenerateTempPassword`. Sharing it matters more than usual here, because
 * the action revokes a safety record: a second copy of this dialog is a second chance for the two
 * to disagree about what pressing the button does.
 *
 * `POST /api/Certification/reset` abandons every in-flight attempt in scope *and* revokes every
 * standing record in scope, in one transaction, and is idempotent — an administrator pressing the
 * button twice on a slow connection is a normal outcome, not an error.
 *
 * The endpoint sits under the `/api/Certification` prefix, so `entity: 'certification'` already
 * invalidates the reads that show it.
 */
export function useResetCertification(): (target: ResetCertificationTarget) => Promise<void> {
  const { t } = useTranslation();

  // `useEntityMutation`, not `useEntityDelete`: this is a POST, and the success copy says "reset"
  // rather than "deleted".
  const reset = useEntityMutation({
    entity: 'certification',
    successMessageKey: 'certifications.reset.success',
    mutationOptions: getPostApiCertificationResetMutationOptions,
  });

  return useCallback(
    async (target: ResetCertificationTarget) => {
      const { userId, institutionId, name, description } = target;

      const { action } = await openModal({
        title: t('certifications.reset.title', { name }),
        description,
        variant: 'error',
        icon: <Icon name="refresh-cw-05" />,
        actions: [
          { label: t('common.cancel'), value: 'cancel' },
          { label: t('certifications.reset.confirm'), value: 'confirm', destructive: true },
        ],
      }).closed;

      // Escape, the overlay and the close button all resolve with `null`, so this compares against
      // the confirm value rather than testing for truthiness.
      if (action !== 'confirm') return;

      reset.mutate({
        data: target.allScopes
          ? { userId, institutionId, allScopes: true }
          : { userId, institutionId, labId: target.labId },
      });
    },
    [reset, t],
  );
}
